import Raven from 'raven-js';
import { useEffect } from 'react';
import { NavMarker } from 'react-rhumb';
import styled from 'styled-components';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { useChosenApps, useChosenAppsData } from 'setup-guide-api/dataFetchingClient/queries/integrations';
import IntegrationsCard from 'setup-guide-components/components/card/tasks/IntegrationsCard';
import CompletedSection from 'setup-guide-components/components/card/tasks/CompletedSection';
import LearnMoreCard from 'setup-guide-components/components/card/tasks/LearnMoreCard';
import TaskViewList from 'setup-guide-components/components/card/tasks/TaskViewList';
import TaskErrorCard from 'setup-guide-components/components/checklist/TaskErrorCard';
import AllTasksProgressBar from 'setup-guide-components/components/checklist/suiteOverview/AllTasksProgressBar';
import IntegrationsSidePanel from 'setup-guide-components/components/modal/IntegrationsSidePanel';
import TaskGroupSidePanel from 'setup-guide-components/components/modal/taskGroup/TaskGroupSidePanel';
import { useTracker } from 'setup-guide-components/hooks/tracking';
import { MAIN_COLUMN_ITEMS_SPACING_PX, MAIN_COLUMN_MAX_WIDTH_PX } from 'setup-guide-components/constants/layoutConstants';
import InviteReminderBannerContainer from '../../containers/InviteReminderBannerContainer';
import DebugModeCardContainer from '../../debug/DebugModeCardContainer';
import EnrollmentFooterContainer from '../EnrollmentFooterContainer';
import InvitedUserAccessErrorPage from '../error/InvitedUserAccessErrorPage';
import TaskGroupErrorBanner from '../error/TaskGroupErrorBanner';
import CrmTemplateBannerContainer from './CrmTemplateBannerContainer';
import { useOnboardingKey, useSelectedView } from '../../hooks/config';
import { useTaskGroupModal } from '../../hooks/modal';
import { useTaskDataInUserGuide } from '../../hooks/task';
import { trackSectionView } from '../../utils/usageTracker';
import { HsfxBannerContainer } from '../hsfx/HsfxBannerContainer';
import { useHasMinimumTaskGroupAmount } from '../nav/hooks/useInvitedUserAccess';
import { SETUP_GUIDE_TWO_COLUMNS_BREAKPOINT_PX } from '../../constants/layoutConstants';
import { MicroappBannerContainer } from '../microapps/MicroappBannerContainer';
import { useIntegrationsSidePanel, useSelectedIntegration } from '../../hooks/integrations';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledLoadingContainer = styled.main.withConfig({
  displayName: "ConsolidatedUserGuideLayout__StyledLoadingContainer",
  componentId: "g9v7l-0"
})(({
  theme
}) => `
  width: 100%;
  max-width: ${MAIN_COLUMN_MAX_WIDTH_PX}px;
  padding-right: ${theme.space[800]};
  display: flex;
  justify-content: center;

  @media only screen and (max-width: ${SETUP_GUIDE_TWO_COLUMNS_BREAKPOINT_PX}px) {
    padding: ${theme.space[0]} ${theme.space[800]};
  }
`);
const StyledContainer = styled.main.withConfig({
  displayName: "ConsolidatedUserGuideLayout__StyledContainer",
  componentId: "g9v7l-1"
})(({
  theme
}) => `
  display: grid;
  align-content: start;
  grid-row-gap: ${MAIN_COLUMN_ITEMS_SPACING_PX}px;
  grid-template-columns: 1fr;
  width: 100%;
  max-width: ${MAIN_COLUMN_MAX_WIDTH_PX}px;
  padding-inline: ${theme.space[800]};
  padding-block-start: ${theme.space[700]};
  padding-block-end: ${theme.space[1000]};

  @media only screen and (max-width: ${SETUP_GUIDE_TWO_COLUMNS_BREAKPOINT_PX}px) {
    margin: ${theme.space[0]} auto;
    padding-inline: ${theme.space[800]};
    padding-block-end: ${theme.space[0]};
  }

  /* Hide the extra divs from NavMarker when tracking visibility */
  & > div[aria-hidden='true'] {
    display: none;
  }
`);
const ConsolidatedUserGuideLayout = () => {
  const onboardingKey = useOnboardingKey();
  const selectedView = useSelectedView();
  const {
    isLoading,
    hasErrored: errorLoadingTasks,
    groupKey
  } = useTaskDataInUserGuide();
  const {
    activeGroupKey,
    showTaskGroupModal,
    hideTaskGroupModal
  } = useTaskGroupModal();
  const hasMinimumTaskGroupAmount = useHasMinimumTaskGroupAmount();
  const {
    hideSidePanel: hideIntegrationsSidePanel,
    showSidePanel: showIntegrationsSidePanel,
    isSidePanelOpen: isIntegrationsSidePanelOpen
  } = useIntegrationsSidePanel();
  const {
    refetch: refetchChosenApps
  } = useChosenAppsData();
  const chosenApps = useChosenApps();
  const [selectedIntegration, setSelectedIntegration] = useSelectedIntegration();
  const {
    trackUserInteraction
  } = useTracker();
  const onTaskGroupClick = taskGroupKey => {
    showTaskGroupModal(taskGroupKey);
  };
  useEffect(() => {
    trackSectionView('user guide main section', onboardingKey || undefined);
    window.scrollTo(0, 0);
  }, [onboardingKey]);
  if (isLoading) {
    return /*#__PURE__*/_jsx(StyledLoadingContainer, {
      children: /*#__PURE__*/_jsx(LoadingSpinner, {})
    });
  }

  // show no access page if user has no tasks
  if (!errorLoadingTasks && !hasMinimumTaskGroupAmount || !onboardingKey) {
    return /*#__PURE__*/_jsx(StyledContainer, {
      children: /*#__PURE__*/_jsx(InvitedUserAccessErrorPage, {
        selectedView: selectedView || undefined
      })
    });
  }
  function onIntegrationsClick(slug) {
    setSelectedIntegration(slug);
    showIntegrationsSidePanel();
    trackUserInteraction(slug !== null ? 'click integrations card app' : 'click integrations card title', slug !== null && slug !== void 0 ? slug : undefined);
  }
  function onHideIntegrationsSidePanel() {
    hideIntegrationsSidePanel();
    setSelectedIntegration(null);
    refetchChosenApps().catch(error => {
      if (error instanceof Error) {
        Raven.captureException(error);
      }
    });
    trackUserInteraction('close integrations side panel');
  }
  return /*#__PURE__*/_jsxs(NavMarker, {
    name: "USER_GUIDE_OVERVIEW_LOADED",
    children: [/*#__PURE__*/_jsxs(StyledContainer, {
      "data-group-key": groupKey,
      "data-onboarding-key": onboardingKey,
      "data-selected-view": selectedView,
      "data-test-id": "user-guide-container",
      children: [/*#__PURE__*/_jsx(TaskGroupErrorBanner, {}), /*#__PURE__*/_jsx(HsfxBannerContainer, {}), /*#__PURE__*/_jsx(MicroappBannerContainer, {}), /*#__PURE__*/_jsx(InviteReminderBannerContainer, {}), /*#__PURE__*/_jsx(AllTasksProgressBar, {
        selectedView: selectedView || undefined
      }), /*#__PURE__*/_jsx(CrmTemplateBannerContainer, {}), /*#__PURE__*/_jsx(DebugModeCardContainer, {}), errorLoadingTasks ? /*#__PURE__*/_jsx(NavMarker, {
        name: "USER_GUIDE_TASK_GROUPS_LOAD_ERROR",
        children: /*#__PURE__*/_jsx(TaskErrorCard, {})
      }) : /*#__PURE__*/_jsx(NavMarker, {
        name: "USER_GUIDE_TASK_GROUPS_LOAD_SUCCESS",
        children: /*#__PURE__*/_jsx(TaskViewList, {
          onTaskGroupClick: onTaskGroupClick,
          onboardingKey: onboardingKey
        })
      }), /*#__PURE__*/_jsx(IntegrationsCard, {
        chosenApps: chosenApps,
        onIntegrationsClick: onIntegrationsClick,
        onboardingKey: onboardingKey
      }), /*#__PURE__*/_jsx(LearnMoreCard, {
        onboardingKey: onboardingKey
      }), /*#__PURE__*/_jsx(CompletedSection, {
        errorLoadingTasks: errorLoadingTasks,
        onTaskGroupClick: onTaskGroupClick,
        onboardingKey: onboardingKey
      }), /*#__PURE__*/_jsx(EnrollmentFooterContainer, {})]
    }), activeGroupKey && /*#__PURE__*/_jsx(TaskGroupSidePanel, {
      groupKey: activeGroupKey,
      onCloseClick: hideTaskGroupModal,
      onNextTaskGroupClick: nextTaskGroupKey => {
        showTaskGroupModal(nextTaskGroupKey);
      }
    }), isIntegrationsSidePanelOpen && /*#__PURE__*/_jsx(IntegrationsSidePanel, {
      offeringSlug: selectedIntegration,
      onClose: onHideIntegrationsSidePanel
    })]
  });
};
export default ConsolidatedUserGuideLayout;